import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LogIn, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

type ProviderId = 'google' | 'apple' | 'facebook';

const PROVIDERS: { id: ProviderId; label: string; blurb: string }[] = [
  { id: 'google', label: 'Google', blurb: 'The broadest reach — most shoppers already have an account.' },
  { id: 'apple', label: 'Apple', blurb: 'Expected on iOS. Apple lets users hide their real email address.' },
  { id: 'facebook', label: 'Facebook', blurb: 'Useful for consumer storefronts; less so for B2B.' },
];

interface SettingsResponse {
  settings: Record<string, unknown>;
}

interface ProvidersResponse {
  providers: ProviderId[];
  configured: ProviderId[];
}

/**
 * Admin → Settings → Social Login
 *
 * Chooses which social buttons appear on this tenant's login and signup pages.
 * The OAuth apps themselves belong to Rumio — tenants never create Google
 * Cloud projects or Apple Services IDs, and there is no redirect URI to
 * register (see functions/src/routes/auth-relay.ts).
 *
 * A provider Rumio hasn't configured is shown but locked: enabling it would
 * only produce a button that fails on click, which is precisely the bug this
 * page exists to end.
 */
export default function SocialLoginSettings() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [configured, setConfigured] = useState<ProviderId[]>([]);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [settingsRes, providersRes] = await Promise.all([
        api.get<SettingsResponse>('/admin/settings'),
        api.get<ProvidersResponse>('/auth/providers'),
      ]);

      const stored = settingsRes.settings?.social_logins;
      const map: Record<string, boolean> = {};
      for (const { id } of PROVIDERS) {
        // No stored row means "offer everything the platform supports" — the
        // behaviour before this page existed. Don't silently switch anyone off.
        map[id] =
          stored && typeof stored === 'object' && !Array.isArray(stored)
            ? (stored as Record<string, boolean>)[id] !== false
            : true;
      }
      setEnabled(map);
      setConfigured(providersRes.configured || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load social login settings');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save(next: Record<string, boolean>) {
    setSaving(true);
    setError('');
    try {
      await api.put('/admin/settings', { settings: { social_logins: next } });
      setEnabled(next);
      // The storefront reads this through useSocialProviders; drop its cache so
      // an admin previewing the login page sees the change immediately.
      queryClient.invalidateQueries({ queryKey: ['social-providers'] });
      setSuccessMsg('Saved');
      setTimeout(() => setSuccessMsg(''), 2500);
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function toggle(id: ProviderId) {
    save({ ...enabled, [id]: !enabled[id] });
  }

  if (loading) return <LoadingSpinner />;

  const noneConfigured = configured.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-ink">
          <LogIn className="h-6 w-6" />
          Social Login
        </h1>
        <p className="mt-1 text-sm text-ink-soft">
          Choose which social sign-in buttons appear on your login and signup pages. Rumio owns and
          maintains the OAuth apps — there's nothing for you to register.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      {successMsg && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4" />
          {successMsg}
        </div>
      )}

      {noneConfigured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Rumio has no social login providers configured right now, so none of these will appear on
            your site. Customers can still sign in with email and password.
          </span>
        </div>
      )}

      <Card>
        <ul className="divide-y divide-line">
          {PROVIDERS.map(({ id, label, blurb }) => {
            const available = configured.includes(id);
            const on = available && enabled[id];
            return (
              <li key={id} className="flex items-center justify-between gap-4 py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink">{label}</span>
                    {!available && (
                      <span className="rounded bg-surface-muted px-1.5 py-0.5 text-xs text-ink-muted">
                        Not available yet
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-ink-soft">
                    {available ? blurb : 'Rumio hasn’t finished setting this provider up.'}
                  </p>
                </div>
                <Button
                  variant={on ? 'primary' : 'secondary'}
                  disabled={!available || saving}
                  onClick={() => toggle(id)}
                >
                  {on ? 'Enabled' : 'Disabled'}
                </Button>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
